import { useCallback, useEffect, useRef, useState } from 'react';
import { Search, Plus, Minus, LocateFixed, Expand, X, MapPin } from 'lucide-react';
import PublicWebDialog from './PublicWebDialog';
import { loadPublicMap } from './loadPublicMap';
import { PLACES, normalizePlace, searchPlaces } from './publicWebData';
import './leaflet-scoped.css';

const MAP_CENTER = [18.48, -69.922];
const HANDLERS = ['dragging', 'touchZoom', 'doubleClickZoom', 'boxZoom', 'keyboard', 'scrollWheelZoom'];
const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

export default function PublicWebMap({ exploring, onExplore, onStatus }) {
  const container = useRef(null), mapRef = useRef(null), layerRef = useRef(null), leafletRef = useRef(null), markerRef = useRef(null);
  const toolsRef = useRef(null), inputRef = useRef(null), exitRef = useRef(null), resultsRef = useRef(null), focusResult = useRef(false);
  const active = useRef(false), geoVersion = useRef(0), geoLock = useRef(false), exploreRef = useRef(exploring);
  const [phase, setPhase] = useState('loading'), [attempt, setAttempt] = useState(0), [zoom, setZoom] = useState(13);
  const [query, setQuery] = useState(''), [searchOpen, setSearchOpen] = useState(false);
  const [geoDialog, setGeoDialog] = useState(false), [geoBusy, setGeoBusy] = useState(false), [located, setLocated] = useState(false);
  const statusRef = useRef(onStatus); statusRef.current = onStatus; exploreRef.current = exploring;
  const announce = useCallback(message => statusRef.current(message), []);
  const options = searchPlaces(query);

  useEffect(() => {
    active.current = true;
    const generation = geoVersion;
    let disposed = false, map, tileTimer;
    setPhase('loading');
    loadPublicMap().then(L => {
      if (disposed) return;
      leafletRef.current = L;
      map = L.map(container.current, {
        zoomControl: false, attributionControl: false, scrollWheelZoom: false, dragging: false,
        touchZoom: false, doubleClickZoom: false, boxZoom: false, keyboard: false,
        zoomAnimation: !reducedMotion(), fadeAnimation: !reducedMotion(), minZoom: 6, maxZoom: 18,
      }).setView(MAP_CENTER, 13);
      mapRef.current = map;
      let loaded = false;
      const tiles = L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19, keepBuffer: 1, updateWhenIdle: true,
        // Browsers retain their normal cache and only disclose this site's origin.
        referrerPolicy: 'strict-origin-when-cross-origin',
      });
      layerRef.current = tiles;
      tiles.on('loading', () => {
        if (disposed) return;
        loaded = false; clearTimeout(tileTimer); setPhase('loading');
        tileTimer = setTimeout(() => { if (!disposed && !loaded) setPhase('error'); }, 12000);
      });
      tiles.on('tileload', () => {
        if (disposed) return;
        loaded = true; clearTimeout(tileTimer); setPhase('ready');
      });
      tiles.on('tileerror', () => { if (!disposed && !loaded) setPhase('error'); });
      tileTimer = setTimeout(() => { if (!disposed && !loaded) setPhase('error'); }, 12000);
      tiles.addTo(map);
      map.on('zoomend', () => { if (!disposed) setZoom(map.getZoom()); });
      HANDLERS.forEach(name => map[name]?.[exploreRef.current ? 'enable' : 'disable']());
      container.current.setAttribute('tabindex', exploreRef.current ? '0' : '-1');
    }).catch(() => { if (!disposed) setPhase('error'); });
    return () => {
      disposed = true; active.current = false; generation.current++; geoLock.current = false;
      clearTimeout(tileTimer); markerRef.current = null; layerRef.current = null; mapRef.current = null;
      if (map) map.remove();
    };
  }, [attempt]);

  useEffect(() => {
    const map = mapRef.current;
    HANDLERS.forEach(name => map?.[name]?.[exploring ? 'enable' : 'disable']());
    if (container.current) container.current.setAttribute('tabindex', exploring ? '0' : '-1');
    map?.invalidateSize(); setSearchOpen(false);
    if (exploring) exitRef.current?.focus();
  }, [exploring]);

  useEffect(() => {
    if (searchOpen && focusResult.current) {
      focusResult.current = false;
      resultsRef.current?.querySelector('button')?.focus();
    }
  }, [searchOpen]);

  useEffect(() => {
    if (!searchOpen) return;
    const outside = event => { if (!toolsRef.current?.contains(event.target)) setSearchOpen(false); };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [searchOpen]);

  const choose = place => {
    // A newer city choice supersedes an outstanding location request.
    geoVersion.current++; geoLock.current = false; setGeoBusy(false);
    markerRef.current?.remove(); markerRef.current = null; setLocated(false);
    if (!mapRef.current) { announce('El mapa no está disponible. Los contactos y las solicitudes siguen funcionando.'); return; }
    mapRef.current.setView([place.lat, place.lng], place.zoom, { animate: !reducedMotion() });
    setQuery(place.name); inputRef.current?.focus(); setSearchOpen(false);
    announce(`Mapa centrado en ${place.name}. Confirma cobertura y disponibilidad con C&A.`);
  };
  const locate = () => {
    setGeoDialog(false);
    if (geoLock.current) return;
    if (!navigator.geolocation || !mapRef.current) { announce('No se puede usar la ubicación ahora. Puedes buscar una ciudad.'); return; }
    geoLock.current = true; setGeoBusy(true);
    const version = ++geoVersion.current;
    const current = () => active.current && geoVersion.current === version;
    const finish = () => { geoLock.current = false; setGeoBusy(false); };
    try { navigator.geolocation.getCurrentPosition(position => {
      if (!current()) return;
      finish();
      const { latitude, longitude } = position?.coords || {};
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude) || Math.abs(latitude) > 90 || Math.abs(longitude) > 180) {
        announce('No recibimos una ubicación válida. Puedes buscar una ciudad.'); return;
      }
      const point = [latitude, longitude], L = leafletRef.current, map = mapRef.current;
      if (!map || !L) return;
      map.setView(point, 14, { animate: !reducedMotion() });
      markerRef.current?.remove();
      markerRef.current = L.circleMarker(point, { radius: 9, color: '#fff', weight: 3, fillColor: '#e41d35', fillOpacity: 1 })
        .addTo(map).bindPopup('Tu ubicación aproximada. No se ha enviado a C&A.');
      setLocated(true); announce('Ubicación aproximada mostrada. No se ha enviado a C&A.');
    }, error => {
      if (!current()) return;
      finish(); announce(error.code === 1 ? 'Permiso de ubicación no concedido. Puedes buscar una ciudad.' : 'No pudimos obtener tu ubicación. Puedes buscar una ciudad.');
    }, { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 });
    } catch {
      if (current()) { finish(); announce('No pudimos usar la ubicación. Puedes buscar una ciudad.'); }
    }
  };
  const clearLocation = () => {
    geoVersion.current++; geoLock.current = false; setGeoBusy(false);
    markerRef.current?.remove(); markerRef.current = null; setLocated(false);
    mapRef.current?.setView(MAP_CENTER, 13, { animate: !reducedMotion() });
    announce('Ubicación borrada de esta página.');
  };
  const mapLabel = phase === 'error' ? 'Mapa no disponible en este momento' : phase === 'loading' ? 'Cargando mapa geográfico…' : 'Mapa geográfico · República Dominicana';
  return <>
    <div ref={container} id="ca-web-map" className="web-map" aria-label="Mapa geográfico de República Dominicana" />
    <div className="map-shade" aria-hidden="true" />
    <div className="map-attribution">© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> contributors · <a href="https://www.openstreetmap.org/fixthemap" target="_blank" rel="noopener noreferrer">Corregir mapa</a></div>
    {phase === 'error' && <div className="map-fallback" role="status"><strong>No se pudo cargar el mapa.</strong><span>Los servicios y contactos siguen disponibles.</span><button type="button" onClick={() => { clearLocation(); setAttempt(value => value + 1); }}>Reintentar mapa</button></div>}
    <div className="map-tools" ref={toolsRef} onKeyDown={event => {
      if (event.key === 'Escape' && searchOpen) { event.stopPropagation(); inputRef.current?.focus(); setSearchOpen(false); }
    }}>
      <form className="search" role="search" onSubmit={event => {
        event.preventDefault();
        const exact = PLACES.find(place => normalizePlace(place.name) === normalizePlace(query));
        if (query.trim() && (exact || options[0])) choose(exact || options[0]); else setSearchOpen(true);
      }}>
        <label className="sr-only" htmlFor="ca-place-search">Buscar ciudad o sector</label><Search className="icon" aria-hidden="true" />
        <input ref={inputRef} id="ca-place-search" type="search" placeholder="Buscar ciudad o sector" autoComplete="off" maxLength={100}
          value={query} onChange={event => { geoVersion.current++; geoLock.current = false; setGeoBusy(false); setQuery(event.target.value); setSearchOpen(true); }} onFocus={() => setSearchOpen(true)}
          aria-describedby="ca-search-hint" aria-expanded={searchOpen} aria-controls="ca-search-results"
          onKeyDown={event => { if (event.key === 'ArrowDown') { event.preventDefault(); if (searchOpen) resultsRef.current?.querySelector('button')?.focus(); else { focusResult.current = true; setSearchOpen(true); } } }} />
        <button aria-label="Buscar en el mapa" type="submit"><Search className="icon" aria-hidden="true" /></button>
      </form>
      <p id="ca-search-hint" className="sr-only">Búsqueda local de una selección de ciudades y sectores. No confirma cobertura ni busca direcciones exactas.</p>
      {searchOpen && <div className="search-results" id="ca-search-results" ref={resultsRef} onKeyDown={event => {
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const buttons = [...resultsRef.current.querySelectorAll('button')], index = buttons.indexOf(document.activeElement);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length;
        buttons[next]?.focus();
      }}>
        {options.map(place => <button key={place.name} type="button" onClick={() => choose(place)}>{place.name}</button>)}
        {!options.length && <p>No encontramos esa zona en esta selección. Prueba una ciudad cercana o explora el mapa.</p>}
      </div>}
      <div className="place-chips"><button type="button" onClick={() => choose(PLACES[0])}>Santo Domingo</button><button type="button" onClick={() => choose(PLACES[1])}>Santiago</button><button type="button" onClick={() => { setQuery(''); setSearchOpen(true); inputRef.current?.focus(); }}>Otra zona</button></div>
    </div>
    <div className="map-controls" aria-label="Controles del mapa">
      <button type="button" aria-label="Acercar mapa" disabled={!mapRef.current || zoom >= 18} onClick={() => mapRef.current?.zoomIn()}><Plus className="icon" aria-hidden="true" /></button>
      <button type="button" aria-label="Alejar mapa" disabled={!mapRef.current || zoom <= 6} onClick={() => mapRef.current?.zoomOut()}><Minus className="icon" aria-hidden="true" /></button>
      <button type="button" aria-label="Usar mi ubicación" aria-busy={geoBusy} disabled={geoBusy} onClick={() => setGeoDialog(true)}><LocateFixed className="icon" aria-hidden="true" /></button>
      <button type="button" aria-label={exploring ? 'Cerrar exploración del mapa' : 'Ampliar y explorar mapa'} aria-pressed={exploring} onClick={() => onExplore(!exploring)}><Expand className="icon" aria-hidden="true" /></button>
      <span className="control-caption">Explorar mapa</span>
    </div>
    {exploring && <><button ref={exitRef} type="button" className="button secondary exit-map" onClick={() => onExplore(false)}><X className="icon" aria-hidden="true" />Volver a la página</button><div className="map-explore-help">Arrastra para explorar · Usa + y − para acercarte<br /><span>Mapa geográfico, sin seguimiento de vehículos</span>{located && <button type="button" onClick={clearLocation}><MapPin className="icon" aria-hidden="true" />Borrar mi ubicación</button>}</div></>}
    <div className="map-status" aria-live="polite">{mapLabel}<span>Sin ubicaciones de grúas en vivo</span></div>
    <PublicWebDialog open={geoDialog} onClose={() => setGeoDialog(false)} title="Tu ubicación en el mapa" className="info-dialog" labelledBy="ca-location-title">
      <p>Si continúas, tu navegador te pedirá permiso para usar tu ubicación. Solo se mostrará en esta página; no se envía una solicitud a C&A.</p>
      <p className="small">Al centrar el mapa, OpenStreetMap recibe las peticiones de mapas de esa zona. La ubicación no se guarda y desaparece al salir de la página. <a href="https://osmfoundation.org/wiki/Privacy_Policy" target="_blank" rel="noopener noreferrer">Privacidad de OpenStreetMap</a>.</p>
      <button type="button" className="button primary" onClick={locate}>Continuar y pedir permiso</button>
      <button type="button" className="text-button" onClick={() => setGeoDialog(false)}>Cancelar</button>
    </PublicWebDialog>
  </>;
}
